<template>
  <Page :title="title" class="c-content-page">
    <section id="container-form-parent">
      <template v-if="$slots.banner">
        <slot name="banner" />
      </template>
      
      <!-- Content Page Form Header -->
      <Header
        :action-disabled="actionDisabled"
        :action-label="actionLabel"
        :cancel-label="cancelLabel"
        :cancelDisabled="cancelDisabled"
        :loading="loading"
        :loading-label="loadingLabel"
        :no-header="noHeader"
        :title="title"
        :isDirty="isDirty"
        :isDirtyOverride="isDirtyOverride"
        :saving="saving"
        :hide-actions="hideActions"
        @save="save"
        @cancel="cancel"
      >

        <template #breadcrumb v-if="$slots.breadcrumb">
          <slot name="breadcrumb" />
        </template>

        <template #header v-if="$slots.header">
          <slot name="header" />
        </template>

        <template #actions v-if="$slots.actions">
          <slot name="actions" />
        </template>

        <template #status v-if="$slots.status">
          <slot name="status" />
        </template>

        <template #overrideCancelButton v-if="$slots.overrideCancelButton">
          <slot
            :disabled="cancelDisabled || actionDisabled || saving"
            :cancel="cancel"
            name="overrideCancelButton"
          />
        </template>

        <template #overrideMainAction v-if="$slots.overrideMainAction">
          <slot
            :disabled="actionDisabled || (!isDirty && !isDirtyOverride)"
            :loading="saving"
            :save="save"
            name="overrideMainAction"
          />
        </template>
      </Header>

      <!-- Content Page Form Body -->
      <Content :loading="loading" :formId="formId" :schema="schema" :initialValue="initialValues">
        <template #content>
          <Form
            v-if="!loading"
            id="content-page-form"
            :form-id="formId"
            :schema="schema"
            :initialValues="initialValues"
            :keepValuesOnUnmount="keepValuesOnUnmount"
            :show-validation-dialog="showValidationDialog"
            @onValidateSuccess="emit('primaryAction', $event)"
            @onValidateFail="handleValidationFail($event)"
            ref="formComponent"
          >
            <template v-slot>
              <slot name="content" />
            </template>
          </Form>
        </template>
      </Content>
    </section>
  </Page>
</template>

<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import { Page } from '../../../core/containers/pages/page';
import type { Schema, TagComponentProp } from './../../../../../types';
import Form from '../form/Form.vue';
import Content from './components/content/Content.vue';
import Header from './components/header/Header.vue';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { isEmpty } from 'lodash-es';
// Define emits
const emit = defineEmits(['primaryAction', 'validationFail', 'cancel']);

// Define props
const props = defineProps({
  title: {
    type: String,
  },
  actionLabel: {
    type: String,
  },
  cancelLabel: {
    type: String,
  },
  actionDisabled: {
    type: Boolean,
    default: false,
  },
  cancelDisabled: {
    type: Boolean,
  },
  hideActions: {
    type: Boolean,
    default: false,
  },
  tagComponents: {
    type: Object as PropType<TagComponentProp[]>,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingLabel: {
    type: String,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  noHeader: {
    type: Boolean,
    default: false,
  },
  formId: {
    type: String,
    required: true,
  },
  schema: {
    type: Object as PropType<Schema>,
    required: true,
    default: () => ({}) as Schema,
  },
  initialValues: {
    type: Object,
  },
  showValidationDialog: {
    type: Boolean,
    default: true,
  },
  keepValuesOnUnmount: {
    type: Boolean,
    default: false,
  },
  isDirtyOverride: {
    type: Boolean,
    default: false,
  },
});

// Define refs for form component
const formComponent = ref<InstanceType<typeof Form>>();

// Handle validation fail event
const handleValidationFail = (args: any[]): void => {
  emit('validationFail', args);
  const invalidFields: HTMLCollectionOf<Element> = document.getElementsByClassName('p-invalid');

  if (!isEmpty(invalidFields)) {
    invalidFields[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

// Submit form
const save = async (): Promise<void> => {
  formComponent.value?.validate();
};

// Computed form dirty state
const isDirty = computed<boolean>(() => formComponent.value?.isDirty);

// Cancel callback function
const cancel = (): void => {
  emit('cancel');
};

const handleNavigationWithDirtyForm = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): void => {
  return formComponent.value?.handleNavigationWithDirtyForm(to, from, next);
};

defineExpose({
  isDirty,
  save,
  cancel,
  handleNavigationWithDirtyForm,
});
</script>
